import React, { useState, useRef, createContext, useContext } from 'react';
import {
  TextField,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  LibraryBooks as LibraryIcon,
  Web as WebIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

type SearchContext = 'Library' | 'Website' | 'Member';

interface GlobalSearchProps {
  onSearch?: (query: string, context: SearchContext) => void;
}

// Create context for website search highlighting
interface WebsiteSearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  highlightedItems: string[];
  setHighlightedItems: (items: string[]) => void;
}

const WebsiteSearchContext = createContext<WebsiteSearchContextType | null>(
  null
);

export const useWebsiteSearch = () => {
  const context = useContext(WebsiteSearchContext);
  if (!context) {
    throw new Error(
      'useWebsiteSearch must be used within a WebsiteSearchProvider'
    );
  }
  return context;
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ onSearch }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchContext, setSearchContext] = useState<SearchContext>('Library');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showWebsiteMessage, setShowWebsiteMessage] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const contextOptions: {
    value: SearchContext;
    label: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: 'Library',
      label: 'Library',
      icon: <LibraryIcon sx={{ fontSize: '0.85rem' }} />,
    },
    {
      value: 'Website',
      label: 'Website',
      icon: <WebIcon sx={{ fontSize: '0.85rem' }} />,
    },
    {
      value: 'Member',
      label: 'Member',
      icon: <PeopleIcon sx={{ fontSize: '0.85rem' }} />,
    },
  ];

  const handleSearch = (query: string, context: SearchContext) => {
    if (!query.trim()) return;

    switch (context) {
      case 'Library':
        // Navigate to library with search query
        navigate(`/library?search=${encodeURIComponent(query)}`);
        break;
      case 'Member':
        // Navigate to user list with search query and type=member-all
        navigate(`/user?search=${encodeURIComponent(query)}&type=member-all`);
        break;
      case 'Website':
        // Handle website search (menu highlighting)
        handleWebsiteSearch(query);
        break;
    }

    if (onSearch) {
      onSearch(query, context);
    }
  };

  const handleWebsiteSearch = (query: string) => {
    // Dispatch a custom event to trigger website search highlighting
    const event = new CustomEvent('websiteSearch', {
      detail: { query: query.toLowerCase().trim() },
    });
    window.dispatchEvent(event);

    // Show a brief message about the search
    setShowWebsiteMessage(true);
    setTimeout(() => setShowWebsiteMessage(false), 2000);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch(searchQuery, searchContext);
    }
  };

  const handleContextChange = (context: SearchContext) => {
    setSearchContext(context);
    setAnchorEl(null);
    setIsMenuOpen(false);
  };

  const handleClear = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getContextIcon = () => {
    const option = contextOptions.find((opt) => opt.value === searchContext);
    return option?.icon || <LibraryIcon sx={{ fontSize: '0.85rem' }} />;
  };

  return (
    <Box sx={{ px: 1.5, py: 0.5 }}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderRadius: 1.5,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <TextField
          ref={inputRef}
          fullWidth
          size="small"
          placeholder={`Search ${searchContext.toLowerCase()}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ minWidth: 'auto' }}>
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {searchQuery && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    sx={{ p: 0.25, minWidth: 'auto' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setIsMenuOpen(true);
                  }}
                  sx={{ p: 0.25, minWidth: 'auto' }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Box>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              minHeight: '32px',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
            '& .MuiInputBase-input': {
              py: 0.5,
            },
          }}
        />

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={() => {
            setAnchorEl(null);
            setIsMenuOpen(false);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: { minWidth: 200 },
          }}
        >
          {contextOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleContextChange(option.value)}
              selected={searchContext === option.value}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
              {searchContext === option.value && (
                <Chip
                  label="Selected"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </MenuItem>
          ))}
        </Menu>

        {/* Context Indicator */}
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Searching:
          </Typography>
          <Chip
            icon={
              <Box
                sx={{ pl: 1, pr: 0.5, display: 'flex', color: 'primary.main' }}
              >
                {getContextIcon()}
              </Box>
            }
            label={searchContext}
            size="small"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setIsMenuOpen(true);
            }}
            sx={{
              backgroundColor: 'background.paper',
              color: 'primary.main',
              fontWeight: 600,
              border: 'none',
              '& .MuiChip-icon': {
                color: 'primary.main',
              },
              '&:hover': {
                backgroundColor: 'background.paper',
                opacity: 0.9,
              },
            }}
          />
        </Box>

        {/* Website Search Message */}
        {showWebsiteMessage && (
          <Alert
            icon={<InfoIcon fontSize="inherit" />}
            severity="success"
            sx={{ mt: 1 }}
            onClose={() => setShowWebsiteMessage(false)}
          >
            Searching menu items for "{searchQuery}" - check the menu for
            highlighted results!
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default GlobalSearch;
